import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { capitalize } from './utils';
import Client from './Client';
import ClientError from './ClientError';

export function createApiHooks({ name, queries = {}, mutations = {} }) {
  return {
    ...createApiQueries(name, queries),
    ...createApiMutations(name, mutations)
  };
}

export function createApiQueries(name, queries) {
  return Object.entries(queries).reduce((acc, [queryName, queryFn]) => {
    const hookName = `use${capitalize(queryName)}Query`;
    const key = `${name}.${queryName}`;
    acc[hookName] = createApiQuery(key, queryFn);

    return acc;
  }, {});
}

export function createApiQuery(key, fetchFn) {
  function getKey(params, gaVersion) {
    return [key, params, gaVersion];
  }

  async function queryFn({ queryKey, signal }) {
    const [, params, gaVersion] = queryKey;

    return await fetchFn(params, { signal }, gaVersion);
  }

  function useApiQuery(params, options, gaVersion) {
    return useQuery({
      queryKey: getKey(params, gaVersion),
      queryFn,
      ...options
    });
  }

  useApiQuery.getQueryKey = getKey;
  useApiQuery.queryFn = queryFn;

  return useApiQuery;
}

export function createApiMutations(name, mutations) {
  return Object.entries(mutations).reduce((acc, [mutationName, mutationFn]) => {
    const hookName = `use${capitalize(mutationName)}Mutation`;
    const key = `${name}.${mutationName}`;
    acc[hookName] = createApiMutation(key, mutationFn);

    return acc;
  }, {});
}

export function createApiMutation(key, mutationFn) {
  const mutationKey = [key];

  function useApiMutation(options) {
    return useMutation({
      mutationKey,
      mutationFn,
      ...options
    });
  }

  useApiMutation.mutationKey = mutationKey;
  useApiMutation.mutationFn = mutationFn;

  return useApiMutation;
}

export function createActionMutation({ mutationKey, mutationFn }, { getQueryKey }) {
  const key = getQueryKey(mutationKey);

  function useActionMutation(options) {
    const queryClient = useQueryClient();
    const fn = useCallback(
      async (payload) => {
        const data = await mutationFn(payload);
        const queryKey = getQueryKey({ instanceId: data.id });
        let action;
        let attempt = 1;

        do {
          try {
            action = await Client.request({
              method: 'GET',
              url: data.statusQueryGetUri
            });
            queryClient.setQueryData(queryKey, action);
            attempt = 1;
          } catch (error) {
            attempt += 1;

            if (attempt > 4) {
              throw error;
            }
          } finally {
            await sleep(2_000 * attempt);
          }
        } while (isActiveAction(action));

        if (action?.customStatus?.status === 'completed') {
          return action;
        }

        throw new Error(action?.customStatus?.message, { cause: action });
      },
      [queryClient]
    );

    return useMutation({
      mutationKey: key,
      mutationFn: fn,
      ...options
    });
  }

  useActionMutation.mutationKey = key;

  return useActionMutation;
}

export async function validateAction(request, response) {
  await Client.defaultValidate(request, response);

  if (response.status !== 202) {
    throw new ClientError('Failed', {
      request,
      response
    });
  }
}

function isActiveAction(action) {
  return ['Pending', 'Running'].includes(action.runtimeStatus);
}

function sleep(ms) {
  const timeout = process.env.NODE_ENV !== 'test' ? ms : 10;

  return new Promise((resolve) => setTimeout(resolve, timeout));
}
